import React, { useEffect, useState, useRef, useCallback } from 'react';
import DataTable from 'react-data-table-component';
import { Button, Form, Modal, Spinner, Alert } from 'react-bootstrap';
import { apiGet, apiPost, apiPut, apiDelete } from '../services/api';
import { useToast } from '../hooks/useToast';
import { toast } from 'react-toastify';
import logger from '../utils/logger';
import '../styles/table.css';

const speak = (text, rate = 0.9) => {
  const synth = window.speechSynthesis;
  synth.cancel(); 
  const voices = synth.getVoices();
  const femaleVoice =
    voices.find(v =>
      v.lang.toLowerCase().includes("en") &&
      (v.name.toLowerCase().includes("zira") ||
        v.name.toLowerCase().includes("anya") ||
        v.name.toLowerCase().includes("female") ||
        v.name.toLowerCase().includes("susan"))
    ) || voices.find(v => v.lang === "en-US");

  const utter = new SpeechSynthesisUtterance(text);
  utter.voice = femaleVoice;
  utter.lang = "en-US";
  utter.rate = rate;
  synth.speak(utter);
};

if (window.speechSynthesis.onvoiceschanged !== undefined) {
  window.speechSynthesis.onvoiceschanged = () => {
    window.speechSynthesis.getVoices();
  };
}

const ConfirmToast = ({ message, onConfirm, onCancel }) => {
  return (
    <div  style={{ maxWidth: '600px' }}>
  <div className="mb-3">
    <h6 className="mb-0 text-center meassage-text">{message}</h6>
  </div>
  <div className="d-flex justify-content-center gap-2">

   
    <Button  className='yes-button' size="sm" onClick={onConfirm}>
      Yes
    </Button>
     <Button  className='No-button' size="sm" onClick={onCancel}>
      No
    </Button>
  </div>
</div>

  );
};

const Department = () => {
  const [departments, setDepartments] = useState([]);
  const [formData, setFormData] = useState({ name: '' });
  const [editId, setEditId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const { showToast, hideToast, toastConfig } = useToast();
  const inputRef = useRef();
  const hasSpokenCloseRef = useRef(false);
const [isDeleteConfirmationActive, setIsDeleteConfirmationActive] = useState(false);
  const BASE_ENDPOINT = '/Department';
  const TOAST_DURATION = 15000; 

  useEffect(() => {
    const timer = setTimeout(() => {
      speak("Welcome to Department Management. You can add, edit or delete departments here.");
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  const fetchDepartments = useCallback(async () => {
    setIsLoading(true);
    try {
      logger.debug('Fetching departments...');
      const response = await apiGet(`${BASE_ENDPOINT}/GetAll`);
      
      if (response?.data?.length) {
        const sortedData = [...response.data].sort((a, b) => b.departmentID - a.departmentID);
        setDepartments(sortedData);
        logger.debug('Departments fetched:', sortedData);
      } else {
        setDepartments([]);
        logger.debug('No departments found.');
      }
    } catch (error) {
      logger.error('Failed to fetch departments:', error);
      showToast('Failed to load departments', 'error', TOAST_DURATION);
      speak("Failed to load departments. Please try again.");
      setDepartments([]);
    } finally {
      setIsLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  useEffect(() => {
    if (isModalOpen && inputRef.current) {
      inputRef.current.focus();
      speak(editId ? "You are now editing a department." : "You are now adding a new department.");
    }
  }, [isModalOpen, editId]);

  const openModal = (data = { name: '' }) => {
    setFormData(data);
    setIsModalOpen(true);
    hasSpokenCloseRef.current = false; 
  };

  const closeModal = () => {
    setFormData({ name: '' });
    setEditId(null);
    setIsModalOpen(false);
    // hideToast();
    
    if (!hasSpokenCloseRef.current) {
      hasSpokenCloseRef.current = true;
    }
  };

  const sanitizeDepartmentName = (input) => {
    return input
      .replace(/[^a-zA-Z\s]/g, '') 
      .replace(/\s+/g, ' ') 
      .trim();
  };

  const handleSpeechInput = () => {
    if (!window.SpeechRecognition && !window.webkitSpeechRecognition) {
      alert("Speech Recognition is not supported in this browser.");
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.lang = "en-US";
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setIsListening(true);
      speak("Listening for department name...");
    };

    recognition.onerror = (event) => {
      setIsListening(false);
      speak("Sorry, I didn't catch that. Please try again.");
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognition.onresult = (event) => {
      let transcript = event.results[0][0].transcript.trim();
      transcript = sanitizeDepartmentName(transcript);
      setFormData({ name: transcript });
      speak(`You said: ${transcript}`);
    };

    recognition.start();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let trimmedName = sanitizeDepartmentName(formData.name?.trim());

    if (!trimmedName) {
      const msg = 'Department name is required!';
      showToast(msg, 'error', TOAST_DURATION);
      speak("Please provide a department name.");
      return;
    }

    try {
      setIsLoading(true);
      const payload = { name: trimmedName };

      if (editId) {
        await apiPut(`${BASE_ENDPOINT}/Update`, { ...payload, departmentID: editId });
        const msg = `Department has been updated successfully!`;
        showToast(msg, 'success', TOAST_DURATION);
        speak(msg);
      } else {
        await apiPost(`${BASE_ENDPOINT}/Create`, payload);
        const msg = `Department has been added successfully!`;
        showToast(msg, 'success', TOAST_DURATION);
        speak(msg);
      }

      await fetchDepartments();
      closeModal();
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApiError = (error) => {
    logger.error('API Error:', error);
    const statusCode = error?.response?.status;
    const apiMessage = error?.response?.data?.status;

    const errorMessages = {
      400: apiMessage || 'Invalid department data',
      404: 'Department not found',
      409: 'This department name already exists. Please choose a different name.',
      500: 'Internal server error'
    };

    const errorMessage = errorMessages[statusCode] || 'Something went wrong. Please try again.';
    const fullMessage = `Operation Failed. ${errorMessage}`;
    
    showToast(fullMessage, 'error', TOAST_DURATION);
    speak(fullMessage);
  };

  const handleEdit = (row) => {
    logger.debug('Editing department:', row);
    setEditId(row.departmentID);
    openModal({ name: row.name });
    speak(`Editing department: ${row.name}. Please provide the new name.`);
  };
  const handleDelete = async (id) => {
     if (isDeleteConfirmationActive) return;
    const departmentToDelete = departments.find(d => d.departmentID === id);
    const confirmMsg = `Are you sure you want to delete the department`;
     setIsDeleteConfirmationActive(true);
    speak(confirmMsg);

        toast.info(
          <ConfirmToast
            message={confirmMsg}
            onConfirm={async () => {
              toast.dismiss();
              try {
                setIsLoading(true);
                await apiDelete(`${BASE_ENDPOINT}/Delete/${id}`);
                const msg = 'Department has been deleted successfully!';
                toast.success(<strong>{msg}</strong>, {
          autoClose: 5000,
          icon: false,
        });
                speak(msg);
                await fetchDepartments();
              } catch (error) {
                handleApiError(error);
              } finally {
                setIsLoading(false);
                 setIsDeleteConfirmationActive(false);
              }
            }}
            onCancel={() => {
              toast.dismiss();
              speak("Delete cancelled.");
              setIsDeleteConfirmationActive(false); 
            }}
          />,
          {
            position: "top-center",
    autoClose: false,
    closeOnClick: false,
    draggable: false,
    closeButton: false,
    icon: false, 
   
    className: 'custom-toast', 
    bodyClassName: 'custom-toast-body', 
    hideProgressBar: true,
          }
        );
  };

  const columns = [
    {
      name: 'ID',
      selector: row => row.departmentID,
      sortable: true,
      omit: true,
    },
    {
     name: 'Name',
    selector: row => row.name,
    sortable: true,
   width: '600px',
   center:true,
    cell: row => <span className="text-capitalize">{row.name}</span>,
   style: {
    borderRight: '1px solid #e5e8ebff!important', 
  },
    },
    {
      name: 'Actions',
    
        center:true,
         className: 'no-border-right',
      cell: (row) => (
        <div className="d-flex gap-2 edit-deleteicon">
          <Button 
            variant="outline-warning custompadding" 
            size="sm me-2" 
            onClick={() => handleEdit(row)}
          >
            <i className="bi bi-pencil"></i>
          </Button>
          <Button 
            variant="outline-danger custompadding" 
            size="sm me-2" 
            onClick={() => handleDelete(row.departmentID)}
            disabled={isDeleteConfirmationActive}
          >
            <i className="bi bi-trash"></i>
          </Button>
        </div>
      ),
      width: '450px'
    },
  ];

  return (
    <div className="container py-4">
    <div className="department-heading-button mb-4 d-flex flex-column flex-lg-row flex-md-row justify-content-center justify-content-lg-between justify-content-md-between align-items-center">
  <h2 className="mb-0 page-heading">Department Management</h2>

  <Button 
    variant="btn-primary" 
    className="btn-fontweight add-departmentbtn mt-2 mt-lg-0" 
    onClick={() => {
      speak("Opening form to add new department.");
      openModal();
    }} 
    disabled={isLoading}
  >
    {isLoading ? <Spinner size="sm" /> : 'Add Department'}
  </Button>
</div>


      {isLoading && !departments.length ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="btn-primary" />
          <p className="mt-2">Loading departments...</p>
        </div>
      ) : (
        <div className="card shadow-sm rounded-4 py-4 px-0 border-0 bg-white mb-custom">
 <DataTable
  className="custom-data-table department-table"
  columns={columns}
  data={departments.map((dept, idx) => ({
    ...dept,
    _rowIndex: idx, 
  }))}
  pagination
  customStyles={{
    table: {
      style: {
        borderRight: '1px solid #dee2e6',
        fontWeight: '600',
        fontSize: '14px',
        backgroundColor: '#f8f9fa',
        textAlign: 'center',
      },
    },
    headRow: {
      style: {
        backgroundColor: '#04364A',
        minHeight: '48px',
      },
    },
    headCells: {
      style: {
        padding: '10px 14px',
        fontSize: '15px',
        fontWeight: '700',
        color: 'white',
        textTransform: 'uppercase',
      },
    },
    cells: {
      style: {
        padding: '10px 14px',
        fontSize: '14px',
        color: '#334155',
      },
    },
  }}
  conditionalRowStyles={[
     {
    when: (row) => row._rowIndex % 2 === 0, 
    style: { backgroundColor: '#f2f2f2' },
  },
  {
    when: (row) => row._rowIndex % 2 !== 0,
    style: { backgroundColor: '#ffffff' },
  },
  ]}
  highlightOnHover
  responsive
  noDataComponent={<Alert variant="info">No departments found</Alert>}
  progressPending={isLoading}
  progressComponent={<Spinner animation="border" variant="btn-primary" />}
/>


        </div>
      )}

      <Modal show={isModalOpen} onHide={closeModal} backdrop="static">
        <Modal.Header closeButton className="modal-customheader">
          <Modal.Title className="custom-modaltitle">{editId ? 'Edit Department' : 'Add Department'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-1">
              <Form.Label className='mb-2'>Department Name</Form.Label>
              <div className="input-group mb-3">
                <Form.Control
                  type="text"
                  ref={inputRef}
                  value={formData.name}
                  onChange={(e) => {
                    const onlyLetters = e.target.value.replace(/[^a-zA-Z\s]/g, '');
                    setFormData({ ...formData, name: onlyLetters });
                  }}
                  placeholder="Enter department name"
                  required
                  disabled={isLoading}
                />
                <Button 
                  variant={isListening ? "danger" : "outline-secondary"} 
                  onClick={handleSpeechInput}
                  disabled={isLoading}
                >
                  <i className={`bi bi-mic${isListening ? "-fill" : ""}`}></i>
                </Button>
              </div>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="custommodal-footer pb-3">
            <Button 
              variant="secondary" 
              className="btn-fontweight cancelbtn-fontsize" 
              onClick={() => {
                speak("Closing department form.");
                closeModal();
              }}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button 
              variant="btn-primary"  
              className="btn-fontweight addbtn-fontsize" 
              type="submit" 
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  {editId ? 'Updating...' : 'Adding...'}
                </>
              ) : editId ? 'Update Department' : 'Add Department'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Alert 
        show={toastConfig.show} 
        variant={toastConfig.type} 
        onClose={hideToast}
        dismissible
        className="position-fixed top-0 end-0 m-3"
        style={{ zIndex: 1050, minWidth: '300px' }}
      >
        {toastConfig.message}
      </Alert>
    </div>
  );
};

export default Department;